import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Clock3,
  CarFront,
  MapPin,
  PhoneCall,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "./Hero.css";
import heroBg from "../../assets/hero-bg.png";
import sedanWhite from "../../assets/sedan-white.png";
import sedanSilver from "../../assets/sedan-silver.png";
import sedanCharcoal from "../../assets/sedan-charcoal.png";
import BookingModal from "../BookingModal";

const PHONE_DISPLAY = "+91 75740 98964";
const PHONE_NUMBER = "917574098964";

const HERO_SLIDES = [
  {
    id: 1,
    car: sedanSilver,
    carAlt: "White premium sedan taxi",
    eyebrow: "SAFE · RELIABLE · AFFORDABLE",
    titleStart: "Hire Taxi From",
    titleHighlight: "Bhole",
    titleEnd: "Cab Service!",
    description: "Comfortable, safe and hassle-free cab journeys across Gujarat. Book local rides, outstation trips and visits to your favourite destinations.",
    label: "Premium Sedan",
  },
  {
    id: 2,
    car: sedanCharcoal,
    carAlt: "Silver sedan taxi for city and outstation travel",
    eyebrow: "YOUR RIDE · YOUR WAY",
    titleStart: "Travel More,",
    titleHighlight: "Worry",
    titleEnd: "Less.",
    description: "Plan your next city ride, family outing or outstation journey with Bhole Cab Service. A comfortable ride is just a call away.",
    label: "Comfort Sedan",
  },
  {
    id: 3,
    car: sedanWhite,
    carAlt: "Dark charcoal premium sedan taxi",
    eyebrow: "GUJARAT TRAVEL PARTNER",
    titleStart: "Your Journey,",
    titleHighlight: "Our",
    titleEnd: "Responsibility.",
    description: "Explore Gujarat with convenient cab service for temple visits, family trips and popular tourist destinations.",
    label: "Executive Sedan",
  },
];

const Hero = ({ onBookNow }) => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const slide = HERO_SLIDES[activeSlide];

  const handleBookNow = () => {
    if (typeof onBookNow === "function") {
      onBookNow();
      return;
    }
    setShowPopup(true);
  };

  const closePopup = () => setShowPopup(false);

  useEffect(() => {
    if (!showPopup) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showPopup]);

  // Automatically advance to the next sedan every 3 seconds.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % HERO_SLIDES.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, []);

  const handleExplore = () => {
    navigate("/services");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello Bhole Cab Service, I would like to enquire about a cab booking."
    );
    window.open(
      `https://wa.me/${PHONE_NUMBER}?text=${message}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const showSlide = (index) => setActiveSlide((index + HERO_SLIDES.length) % HERO_SLIDES.length);

  return (
    <>
      <section
        className="bh-hero"
        style={{ "--bh-hero-image": `url("${heroBg}")` }}
        aria-label="Bhole Cab Service taxi booking"
      >
        <div className="bh-hero__image-wash" aria-hidden="true" />
        {/* <div className="bh-hero__yellow-shape" aria-hidden="true" /> */}

        <div className="bh-hero__car-stage" aria-live="polite">
          <div className="bh-hero__car-glow" aria-hidden="true" />
          <img
            key={slide.car}
            className="bh-hero__car"
            src={slide.car}
            alt={slide.carAlt}
          />
          <span className="bh-hero__car-label"><CarFront size={15} /> {slide.label}</span>
        </div>

        <div className="bh-hero__content-wrap">
          <div className="bh-hero__content" key={slide.id}>
            <div className="bh-hero__eyebrow">
              <span className="bh-hero__eyebrow-mark" />
              {slide.eyebrow}
            </div>

            <h1 className="bh-hero__title">
              {slide.titleStart}
              <br />
              <span>{slide.titleHighlight}</span> {slide.titleEnd}
            </h1>

            <p className="bh-hero__description">{slide.description}</p>

            <div className="bh-hero__actions">
              <button type="button" className="bh-hero__button bh-hero__button--primary" onClick={handleBookNow}>
                Book Now <ArrowRight size={17} aria-hidden="true" />
              </button>
              <button type="button" className="bh-hero__button bh-hero__button--secondary" onClick={handleExplore}>
                Explore Services
              </button>
            </div>

            <div className="bh-hero__benefits" aria-label="Cab service highlights">
              <div className="bh-hero__benefit">
                <span className="bh-hero__benefit-icon"><Clock3 size={17} /></span>
                <span><strong>Flexible Booking</strong><small>Plan your journey</small></span>
              </div>
              <span className="bh-hero__benefit-divider" />
              <div className="bh-hero__benefit">
                <span className="bh-hero__benefit-icon"><CarFront size={18} /></span>
                <span><strong>Comfortable Rides</strong><small>Sedan options</small></span>
              </div>
              <span className="bh-hero__benefit-divider" />
              <div className="bh-hero__benefit">
                <span className="bh-hero__benefit-icon"><ShieldCheck size={18} /></span>
                <span><strong>Easy Enquiry</strong><small>Call or WhatsApp</small></span>
              </div>
            </div>
          </div>
        </div>

        <div className="bh-hero__slider-controls" aria-label="Choose a hero car">
          <button type="button" className="bh-hero__slider-arrow" onClick={() => showSlide(activeSlide - 1)} aria-label="Previous car">
            <ChevronLeft size={17} />
          </button>
          {HERO_SLIDES.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={`bh-hero__slider-dot ${activeSlide === index ? "is-active" : ""}`}
              onClick={() => showSlide(index)}
              aria-label={`Show car ${item.id}: ${item.label}`}
              aria-pressed={activeSlide === index}
            >
              {item.id}
            </button>
          ))}
          <button type="button" className="bh-hero__slider-arrow" onClick={() => showSlide(activeSlide + 1)} aria-label="Next car">
            <ChevronRight size={17} />
          </button>
        </div>

        <div className="bh-hero__quick-contact">
          <a href={`tel:+${PHONE_NUMBER}`} className="bh-hero__quick-contact-link">
            <span className="bh-hero__quick-contact-icon"><PhoneCall size={17} /></span>
            <span><small>Call for a cab</small><strong>{PHONE_DISPLAY}</strong></span>
          </a>
          <button type="button" onClick={openWhatsApp} className="bh-hero__whatsapp" aria-label="Enquire on WhatsApp">
            <MessageCircle size={17} /> WhatsApp
          </button>
        </div>

        <div className="bh-hero__location-tag"><MapPin size={14} /> Gujarat, India</div>
      </section>

      <BookingModal isOpen={showPopup} onClose={closePopup} phone={`+${PHONE_NUMBER}`} />
    </>
  );
};

export default Hero;
